import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, Plus } from 'lucide-react';
import { projects } from '../data/projects';
import { Lightbox } from './Lightbox';
import { Reveal } from './Reveal';
import { Eyebrow } from './Eyebrow';
const layout = ['lg:col-span-7 aspect-[4/5] lg:aspect-[4/3]', 'lg:col-span-5 aspect-[3/2] lg:mt-20', 'lg:col-span-5 aspect-[4/5]', 'lg:col-span-7 aspect-[3/2] lg:mt-16', 'lg:col-span-7 aspect-[3/2]', 'lg:col-span-5 aspect-[4/5] lg:mt-12'];
export function Showcase() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const close = () => setOpenIndex(null);
  const prev = () => setOpenIndex(i => i === null ? i : (i - 1 + projects.length) % projects.length);
  const next = () => setOpenIndex(i => i === null ? i : (i + 1) % projects.length);
  return <section id="work" className="bg-cream-200 py-24 sm:py-32" data-id="element-311" data-mp="h4cq6izv2vog">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8" data-id="element-312" data-mp="h4ut1yh1485c">
        <Reveal data-id="element-313" data-mp="3ja1ic8hg6ow8">
          <div className="max-w-3xl" data-id="element-314" data-mp="3jal1ttmqxlcd">
            <Eyebrow data-id="element-315" data-mp="3j7fbyc6amffl">Our Work</Eyebrow>
            <h2 className="mt-6 font-display text-[clamp(2.1rem,5vw,3.8rem)] leading-[1.02] tracking-[-0.02em] text-navy-900" data-id="element-316" data-mp="3e9x6ivrhiicc">
              Craftsmanship <span className="italic text-brass-dark" data-id="element-317" data-mp="3a93m4cd93lo7">You Can See.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-700/70" data-id="element-318" data-mp="35bbhqivf6734">
              A selection of recent remodeling and painting projects for Kentucky
              homeowners. Tap any project to see the details.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8" data-id="element-319" data-mp="3t6wggq9t6n9z">
          {projects.map((project, i) => <Reveal key={project.id} delay={i % 2 * 0.08} className={`sm:col-span-1 ${layout[i] ?? 'lg:col-span-6 aspect-[3/2]'}`} data-id="element-320" data-mp="3o8379a1k1ifm" data-mpi={i}>
              <motion.button type="button" onClick={() => setOpenIndex(i)} whileHover={{
            y: -6
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="group relative block h-full w-full overflow-hidden rounded-2xl text-left shadow-[0_25px_60px_-40px_rgba(13,27,42,0.8)]" aria-label={`View ${project.type} in ${project.location}`} data-id="element-321" data-mp="kr4co60hmbmw">
                <img src={project.image} alt={`${project.type} in ${project.location}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" data-id="element-322" data-mp="kxww850pqd6f" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/25 to-transparent opacity-90" data-id="element-323" data-mp="fz29wt3je6hk" />

                <span className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white opacity-0 backdrop-blur transition-all duration-400 group-hover:opacity-100" data-id="element-324" data-mp="2695cdb15bwsa">
                  <Plus className="h-5 w-5" aria-hidden="true" data-id="element-325" data-mp="3qu20cvi5do8i" />
                </span>

                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8" data-id="element-326" data-mp="8fay1lhryxc5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brass-light" data-id="element-327" data-mp="3ob2ajke6lb8">
                    {project.type}
                  </p>
                  <p className="mt-2 flex items-center gap-2 text-[13px] text-white/70" data-id="element-328" data-mp="ibnqyh5hx75u">
                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" data-id="element-329" data-mp="ddcgq5bagun3" />
                    {project.location}
                  </p>
                  <p className="mt-3 max-w-md text-[15px] leading-relaxed text-white/80 opacity-0 transition-all duration-500 group-hover:opacity-100 lg:translate-y-2 lg:group-hover:translate-y-0" data-id="element-330" data-mp="rbj6ey633obo">
                    {project.description}
                  </p>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-white/50" data-id="element-331" data-mp="2o7gx2u64tyas">
                    {project.services.join(' · ')}
                  </p>
                </div>
              </motion.button>
            </Reveal>)}
        </div>
      </div>

      <AnimatePresence data-id="element-332" data-mp="2zl0kh7605ikt">
        {openIndex !== null && <Lightbox project={projects[openIndex]} onClose={close} onPrev={prev} onNext={next} data-id="element-333" data-mp="2zjfpxhjotzmy" />}
      </AnimatePresence>
    </section>;
}